/**
 * Onboarding Interviewer API service
 *
 * Handles all API calls for the link-based interviewer flow:
 * - Interviewer management
 * - File upload URLs
 * - Session listing and detail
 */

import apiClient, { ApiResponse } from './client';

// ========================================
// ENUMS
// ========================================

export type SessionPhase =
  | 'asset_upload'
  | 'processing'
  | 'planning'
  | 'interviewing'
  | 'reviewing'
  | 'generating'
  | 'completed'
  | 'failed';

export type AssetType = 'sales_deck' | 'video' | 'url';

export type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type TemplateStatus = 'draft' | 'ready_for_review' | 'approved' | 'applied';

// ========================================
// INTERFACES
// ========================================

export interface InterviewEntry {
  id: string;
  speaker: 'interviewer' | 'user';
  content: string;
  topic_id: string | null;
  question_type: string | null;
  sequence_number: number;
  created_at: string;
}

export interface InterviewEntryListResponse {
  entries: InterviewEntry[];
  total_count: number;
}

// ========================================
// INTERVIEWER INTERFACES (Link-based flow)
// ========================================

export interface InterviewerAsset {
  id: string;
  asset_type: string;
  filename: string | null;
  url: string | null;
  processing_status: string;
  processing_error: string | null;
  created_at: string;
  processed_at: string | null;
}

export interface FileUploadInfo {
  storage_path: string;
  filename: string;
  file_type: 'pdf' | 'video';
}

export interface InterviewerKnowledgeBatchSummary {
  id: string;
  status: string;
  trigger_type: string;
  knowledge_asset_ids: string[];
  base_profile_version: number;
  merged_profile_version: number | null;
  prompt_impact?: InterviewerPromptImpactSummary | null;
  created_at: string;
}

export interface InterviewerKnowledgeProfileSummary {
  version: number;
  confidence_by_area: Record<string, number>;
  open_questions: Array<Record<string, any>>;
  confirmed_topic_summaries: Record<string, Record<string, any>>;
}

export interface InterviewerPromptImpactSummary {
  status?: string | null;
  impact_level?: 'none' | 'recommended' | 'required' | string | null;
  summary?: string | null;
  domains?: string[];
  impacted_prompt_fields?: string[];
  reasons?: string[];
  assessed_at?: string | null;
  profile_version?: number | null;
  last_prompt_profile_version?: number | null;
}

export interface ResolveOpenQuestionPayload {
  question: Record<string, any>;
  answer: string;
  topic_id?: string | null;
  topic_name?: string | null;
}

export interface RemoveInterviewerKnowledgePayload {
  remove_website?: boolean;
  remove_knowledge_base?: boolean;
  selected_video_ids?: string[];
  knowledge_asset_ids?: string[];
}

export interface KnowledgeRemovalRiskSummary {
  topic_id: string;
  topic_name: string;
  change_type: 'removed' | 'changed' | string;
  current_summary?: string | null;
  projected_summary?: string | null;
}

export interface SourceRemovalPreview {
  interviewer_id: string;
  removed_sources: Array<Record<string, any>>;
  remaining_sources: Array<Record<string, any>>;
  current_profile: InterviewerKnowledgeProfileSummary;
  projected_profile: InterviewerKnowledgeProfileSummary;
  knowledge_at_risk: KnowledgeRemovalRiskSummary[];
  reopened_questions: Array<Record<string, any>>;
  prompt_impact?: InterviewerPromptImpactSummary | null;
}

export interface Interviewer {
  id: string;
  organization_id: string;
  token: string;
  website_url: string | null;
  knowledge_base_url: string | null;
  processing_status: string;
  processing_error: string | null;
  name: string | null;
  description: string | null;
  is_primary: boolean;
  is_active: boolean;
  interview_link: string | null;
  session_count: number;
  reused_existing?: boolean;
  created_at: string;
  updated_at: string;
  assets?: InterviewerAsset[];
  selected_video_ids?: string[];
  knowledge_asset_ids?: string[];
  config_generation_status?: string | null;
  prompt_status?: string | null;
  last_prompt_profile_version?: number | null;
  prompt_impact_summary?: InterviewerPromptImpactSummary | null;
}

export interface InterviewerDetail extends Interviewer {
  total_topics: number;
  initial_confidence: Record<string, number>;
  knowledge_profile?: InterviewerKnowledgeProfileSummary | null;
  ingestion_batches: InterviewerKnowledgeBatchSummary[];
}

export type ReingestMode = 'refresh_sources' | 'rebuild_profile' | 'full_rebuild';

export interface InterviewerListResponse {
  interviewers: Interviewer[];
  total_count: number;
}

export interface InterviewerSessionSummary {
  id: string;
  interviewee_name: string | null;
  interviewee_email: string | null;
  phase: string;
  current_topic_index: number;
  total_topics: number;
  overall_confidence: number;
  summary: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface InterviewerSessionListResponse {
  sessions: InterviewerSessionSummary[];
  total_count: number;
}

export interface SessionDetailResponse {
  id: string;
  interviewer_id: string | null;
  interviewee_name: string | null;
  interviewee_email: string | null;
  phase: string;
  current_topic_index: number;
  total_topics: number;
  field_confidence: Record<string, number>;
  summary: string | null;
  entries: InterviewEntry[];
  created_at: string;
  completed_at: string | null;
}

// ========================================
// SSE EVENT TYPES
// ========================================

export interface InterviewStreamEvent {
  event_type: 'content' | 'progress' | 'confidence_update' | 'topic_change' | 'complete' | 'error' | 'done';
  data: {
    content?: string;
    topic_index?: number;
    topic_name?: string;
    field_confidence?: Record<string, number>;
    overall_confidence?: number;
    next_question?: string;
    is_complete?: boolean;
    message?: string;
    error?: string;
  };
}

// ========================================
// SERVICE CLASS
// ========================================

class OnboardingService {
  private baseEndpoint = '/api/onboarding';

  // ========================================
  // INTERVIEWER METHODS (Link-based flow)
  // ========================================

  /**
   * Request a signed upload URL for an interviewer file attachment
   */
  async requestInterviewerUploadUrl(
    organizationId: string,
    filename: string,
    fileType: 'pdf' | 'video'
  ): Promise<ApiResponse<{ storage_path: string; upload_url: string }>> {
    return apiClient.post<{ storage_path: string; upload_url: string }>(
      `${this.baseEndpoint}/interviewers/upload-url`,
      {
        organization_id: organizationId,
        filename,
        file_type: fileType,
      }
    );
  }

  /**
   * Upload file to signed URL
   */
  async uploadFileToSignedUrl(uploadUrl: string, file: File): Promise<boolean> {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });
      return response.ok;
    } catch (error) {
      console.error('Failed to upload file:', error);
      return false;
    }
  }

  /**
   * Create a new interviewer link
   */
  async createInterviewer(
    organizationId: string,
    websiteUrl?: string,
    knowledgeBaseUrl?: string,
    fileUploads?: FileUploadInfo[],
    name?: string,
    selectedVideoIds?: string[],
    knowledgeAssetIds?: string[]
  ): Promise<ApiResponse<Interviewer>> {
    return apiClient.post<Interviewer>(`${this.baseEndpoint}/interviewers`, {
      organization_id: organizationId,
      website_url: websiteUrl || null,
      knowledge_base_url: knowledgeBaseUrl || null,
      file_uploads: fileUploads || null,
      name: name || null,
      selected_video_ids: selectedVideoIds || null,
      knowledge_asset_ids: knowledgeAssetIds || null,
    });
  }

  /**
   * List all interviewers for an organization
   */
  async listInterviewers(organizationId: string): Promise<ApiResponse<InterviewerListResponse>> {
    return apiClient.get<InterviewerListResponse>(
      `${this.baseEndpoint}/interviewers`,
      { organization_id: organizationId }
    );
  }

  /**
   * Get interviewer by ID
   */
  async getInterviewer(interviewerId: string): Promise<ApiResponse<InterviewerDetail>> {
    return apiClient.get<InterviewerDetail>(`${this.baseEndpoint}/interviewers/${interviewerId}`);
  }

  /**
   * Attach new knowledge to an existing interviewer as a new ingestion batch
   */
  async ingestInterviewerKnowledge(
    interviewerId: string,
    payload: {
      website_url?: string | null;
      knowledge_base_url?: string | null;
      file_uploads?: FileUploadInfo[] | null;
      selected_video_ids?: string[] | null;
      knowledge_asset_ids?: string[] | null;
    }
  ): Promise<ApiResponse<Interviewer>> {
    return apiClient.post<Interviewer>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/knowledge`,
      {
        website_url: payload.website_url || null,
        knowledge_base_url: payload.knowledge_base_url || null,
        file_uploads: payload.file_uploads || null,
        selected_video_ids: payload.selected_video_ids || null,
        knowledge_asset_ids: payload.knowledge_asset_ids || null,
      }
    );
  }

  /**
   * Re-run an interviewer's existing sources and/or rebuild canonical profile history
   */
  async reingestInterviewer(
    interviewerId: string,
    mode: ReingestMode = 'full_rebuild'
  ): Promise<ApiResponse<Interviewer>> {
    return apiClient.post<Interviewer>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/reingest`,
      { mode }
    );
  }

  /**
   * Resolve canonical open questions directly from the platform
   */
  async resolveOpenQuestions(
    interviewerId: string,
    resolutions: ResolveOpenQuestionPayload[]
  ): Promise<ApiResponse<Interviewer>> {
    return apiClient.post<Interviewer>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/resolve-open-questions`,
      { resolutions }
    );
  }

  /**
   * Preview the impact of removing sources from the training workspace
   */
  async previewInterviewerKnowledgeRemoval(
    interviewerId: string,
    payload: RemoveInterviewerKnowledgePayload
  ): Promise<ApiResponse<SourceRemovalPreview>> {
    return apiClient.post<SourceRemovalPreview>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/knowledge-removal-preview`,
      {
        remove_website: payload.remove_website || false,
        remove_knowledge_base: payload.remove_knowledge_base || false,
        selected_video_ids: payload.selected_video_ids || [],
        knowledge_asset_ids: payload.knowledge_asset_ids || [],
      }
    );
  }

  /**
   * Remove one or more sources from the training workspace
   */
  async removeInterviewerKnowledge(
    interviewerId: string,
    payload: RemoveInterviewerKnowledgePayload
  ): Promise<ApiResponse<Interviewer>> {
    return apiClient.post<Interviewer>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/remove-knowledge`,
      {
        remove_website: payload.remove_website || false,
        remove_knowledge_base: payload.remove_knowledge_base || false,
        selected_video_ids: payload.selected_video_ids || [],
        knowledge_asset_ids: payload.knowledge_asset_ids || [],
      }
    );
  }

  /**
   * Consolidate legacy interviewer records into the target training workspace
   */
  async consolidateInterviewers(
    interviewerId: string,
    sourceInterviewerIds: string[]
  ): Promise<ApiResponse<Interviewer>> {
    return apiClient.post<Interviewer>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/consolidate`,
      { source_interviewer_ids: sourceInterviewerIds }
    );
  }

  /**
   * List sessions for an interviewer
   */
  async listInterviewerSessions(interviewerId: string): Promise<ApiResponse<InterviewerSessionListResponse>> {
    return apiClient.get<InterviewerSessionListResponse>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/sessions`
    );
  }

  /**
   * Get session detail with transcript
   */
  async getInterviewerSessionDetail(
    interviewerId: string,
    sessionId: string
  ): Promise<ApiResponse<SessionDetailResponse>> {
    return apiClient.get<SessionDetailResponse>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/sessions/${sessionId}`
    );
  }

  /**
   * Delete a single interview session (only non-completed sessions)
   */
  async deleteInterviewerSession(
    interviewerId: string,
    sessionId: string
  ): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(
      `${this.baseEndpoint}/interviewers/${interviewerId}/sessions/${sessionId}`
    );
  }

  /**
   * Delete an interviewer
   */
  async deleteInterviewer(interviewerId: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`${this.baseEndpoint}/interviewers/${interviewerId}`);
  }

  /**
   * Generate SalesAgent config from interviewer data and auto-apply.
   * Calls the sales agent generate-config endpoint with interviewer context.
   * Returns immediately — frontend polls interviewer detail for status.
   */
  async generateConfig(
    organizationId: string,
    websiteUrl: string
  ): Promise<ApiResponse<{ job_id: string; status: string }>> {
    return apiClient.post<{ job_id: string; status: string }>(
      `/api/organizations/${organizationId}/sales-agent/generate-config`,
      {
        company_website_url: websiteUrl,
      }
    );
  }
}

const onboardingService = new OnboardingService();
export default onboardingService;
